import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useGetVehicleDealerStockDealerDropdownQuery } from '../../../api/endpoints/karsanStockVehiclesReport';
import { useLanguage } from '../../../contexts/LanguageContext';

const SEARCH_DEBOUNCE_MS = 350;

const mapDealerOption = (dealer) => ({
    id: dealer.id,
    sapDealerId: dealer.sapDealerId,
    name: dealer.name,
    text: dealer.translatedText || dealer.name,
    displayValue: dealer.name
});

/**
 * Yurt içi (Land1 = TR) ve araç satış (spart = 10) bayileri; yalnızca Bayi Stoğumdaki Araçlar sekmesi için.
 * @param {Object} props
 * @param {Object|null} props.value - Seçili bayi option'ı ({ id, sapDealerId, ... })
 * @param {Function} props.onChange - Seçilen option (veya null) ile çağrılır
 */
const DealerStockDealerDropdown = ({ value, onChange, className = '', disabled = false, placeholder, rowCount = 50 }) => {
    const { th } = useLanguage();
    const [filterValue, setFilterValue] = useState('');
    const [searchText, setSearchText] = useState('');
    const debounceRef = useRef(null);

    const { currentData, isError, isFetching } = useGetVehicleDealerStockDealerDropdownQuery({ SearchText: searchText, RowCount: rowCount });

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    const handleFilterInputChange = useCallback((val) => {
        setFilterValue(val);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSearchText((val ?? '').trim());
        }, SEARCH_DEBOUNCE_MS);
    }, []);

    const selectedOption = value && typeof value === 'object' ? value : null;

    const options = useMemo(() => {
        const list = isError ? [] : (currentData ?? []).map(mapDealerOption);
        if (selectedOption && !list.some((o) => o.sapDealerId?.toString() === selectedOption.sapDealerId?.toString())) {
            return [selectedOption, ...list];
        }
        return list;
    }, [currentData, isError, selectedOption]);

    const handleSelectionChange = (e) => {
        onChange(e.value || null);
    };

    const handleHide = () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setFilterValue('');
        setSearchText('');
    };

    const filterTemplate = useCallback(
        () => (
            <div className="p-dropdown-filter-container" onMouseDown={(e) => e.stopPropagation()}>
                <InputText
                    value={filterValue}
                    onChange={(e) => handleFilterInputChange(e.target.value)}
                    className="p-dropdown-filter w-full"
                    placeholder={th('karsanStockVehiclesReport.dealerSearchPlaceholder')}
                    autoFocus
                    disabled={disabled}
                    onKeyDown={(e) => e.stopPropagation()}
                    onKeyUp={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                />
                <span className="p-dropdown-filter-icon pi pi-search" />
            </div>
        ),
        [filterValue, handleFilterInputChange, disabled, th]
    );

    const emptyMessage = filterValue.trim() ? th('karsanStockVehiclesReport.dealerNotFound') : th('karsanStockVehiclesReport.dealerEmpty');

    return (
        <Dropdown
            value={selectedOption}
            options={options}
            onChange={handleSelectionChange}
            onHide={handleHide}
            optionLabel="displayValue"
            dataKey="sapDealerId"
            filter
            filterTemplate={filterTemplate}
            showClear
            className={className}
            placeholder={placeholder || th('karsanStockVehiclesReport.dealerFilterPlaceholder')}
            loading={isFetching}
            disabled={disabled}
            emptyMessage={emptyMessage}
            emptyFilterMessage={emptyMessage}
            virtualScrollerOptions={{ itemSize: 38 }}
            itemTemplate={(option) => (
                <div>
                    <span className="font-bold">{option.sapDealerId}</span> {option.text}
                </div>
            )}
        />
    );
};

export default DealerStockDealerDropdown;
